import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Building2,
    Mail,
    Phone,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import apiendpoints from "../apiendpoints.json";
import Warning from "../components/Warning";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import useRecaptcha from "../hooks/useRecaptcha";
import CaptchaV2 from "../components/CaptchaV2";
import recaptchaRepository from "../repositories/recaptchaRepository";
import Banner from "../components/Banner";

const CURRENCIES = ["INR"];
const RECAPTCHA_ACTION = "payment_page_load";
const GST_RATE = 0.18;
const PLATFORM_FEE_RATE = 0.0236;

export default function SecurePayment() {
    const location = useLocation();
    const fullNameRef = useRef(null);
    const emailRef = useRef(null);
    const phoneRef = useRef(null);
    const currencyRef = useRef(null);
    const amountRef = useRef(null);
    const agreementRef = useRef(null);
    const captchaV2Ref = useRef(null);

    const [captchaV2Token, setCaptchaV2Token] = useState("");
    const [showV2, setShowV2] = useState(false);
    const [captchaReady, setCaptchaReady] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);

    const passedPrice = location.state?.price;
    const { getV3Token, isReady: v3Ready } = useRecaptcha();

    const fieldRefs = {
        fullName: fullNameRef,
        email: emailRef,
        currency: currencyRef,
        amount: amountRef,
        phone: phoneRef,
        agreement: agreementRef,
        captchaV2: captchaV2Ref,
    };

    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        phone: "",
        currency: "INR",
        amount: passedPrice || "",
        companyName: "",
        agreement: false,
    });

    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        const nextValue = type === "checkbox" ? checked : value;

        setFormData((prev) => ({
            ...prev,
            [name]: nextValue,
        }));

        const fieldError = validateField(name, nextValue);
        setErrors((prev) => ({ ...prev, [name]: fieldError }));
    };

    // Run v3 verify on page load once the v3 script is ready.
    useEffect(() => {
        let cancelled = false;
        let ranOnce = false;

        const runInitialCaptcha = async () => {
            if (cancelled || ranOnce) return;
            // eslint-disable-next-line no-console
            console.log("[SecurePayment] initial v3 verify starting…");
            try {
                const token = await getV3Token(RECAPTCHA_ACTION);
                if (cancelled || ranOnce) return;

                const result = await recaptchaRepository.verify({
                    version: "v3",
                    token,
                    action: RECAPTCHA_ACTION,
                });
                ranOnce = true;
                if (cancelled) return;

                // eslint-disable-next-line no-console
                console.log(
                    "[SecurePayment] initial verify result:",
                    result
                );

                const wrapperSuccess = result?.success === true;
                const innerV2Required =
                    result?.data?.v2Required === true;
                const innerSuccess =
                    result?.data?.success === true;

                if (wrapperSuccess && innerSuccess && !innerV2Required) {
                    setIsVerified(true);
                    setShowV2(false);
                } else {
                    setShowV2(true);
                    setIsVerified(false);
                }
                setCaptchaReady(true);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error(
                    "[SecurePayment] Initial reCAPTCHA verify failed:",
                    err
                );
                if (!cancelled) {
                    setShowV2(true);
                    setCaptchaReady(true);
                }
            }
        };

        if (v3Ready) {
            runInitialCaptcha();
        } else {
            // Wait briefly for v3 script/provider to mount, then retry once.
            const t = setTimeout(() => {
                if (!cancelled) runInitialCaptcha();
            }, 500);
            return () => {
                cancelled = true;
                clearTimeout(t);
            };
        }

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [v3Ready]);

    const ledger = useMemo(() => {
        const amount = Number(formData.amount || 0);
        const subtotal = amount;
        const gst = subtotal * GST_RATE;
        const platformFee = subtotal * PLATFORM_FEE_RATE;
        const total = subtotal + gst + platformFee;
        return { amount, subtotal, gst, platformFee, total };
    }, [formData.amount]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        const nextErrors = validateForm(formData);
        setErrors(nextErrors);

        if (Object.keys(nextErrors).length > 0) {
            const firstErrorField = Object.keys(nextErrors)[0];
            const fieldRef = fieldRefs[firstErrorField];
            if (fieldRef?.current) {
                fieldRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
                if (firstErrorField !== "agreement") {
                    fieldRef.current.focus();
                }
            }
            return;
        }

        if (!isVerified) {
            alert("Please complete the captcha verification before proceeding.");
            return;
        }

        if (isVerifying) return;

        try {
            let captchaToken;
            let captchaVersion;

            if (showV2) {
                if (!captchaV2Token) {
                    setErrors((prev) => ({
                        ...prev,
                        captchaV2: "Please verify you are not a robot.",
                    }));
                    return;
                }
                captchaToken = captchaV2Token;
                captchaVersion = "v2";
            } else {
                captchaToken = await getV3Token("payment");
                captchaVersion = "v3";
            }

            const payload = {
                fullName: formData.fullName,
                email: formData.email,
                phone: formData.phone,
                companyName: formData.companyName,
                currency: formData.currency,
                baseAmount: ledger.total,
                serviceAmount: ledger.amount,
                subtotal: ledger.subtotal,
                gstAmount: ledger.gst,
                platformFee: ledger.platformFee,
                totalAmount: ledger.total,
                captchaToken,
                captchaVersion,
            };

            const response = await fetch(
                `${apiendpoints.equityUrl}/api/CreatePayment`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                }
            );

            const payuData = await response.json();

            if (!response.ok) {
                alert(payuData.message || "Payment initiation failed");
                return;
            }

            redirectToPayU(payuData);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            alert(err.message || "Something went wrong.");
        }
    };

    const redirectToPayU = (payuData) => {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = payuData.paymentUrl;
        form.style.display = "none";

        Object.entries(payuData).forEach(([key, value]) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key;
            input.value = value;
            form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
    };

    return (

          <>
      <Banner />
        <div className="secure-payment-page min-h-screen overflow-x-hidden overflow-y-auto bg-gradient-to-b from-[#f7fbff] via-white to-[#f2fbf5]">

            <section className="hidden lg:block">
                <Navbar />
                <Warning />
            </section>
            <section className="block lg:hidden">
                <Sidebar />
            </section>

            {/* Main */}

            <div className="mx-auto max-w-[1280px] px-3 py-3 sm:px-4 sm:py-4">

                <form
                    onSubmit={handleSubmit}
                    className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-stretch"
                >

                    {/* LEFT SIDE */}

                    <div>

                        <h1 className="text-2xl font-bold leading-tight sm:text-[24px]">
                            Secure Payment
                        </h1>

                        <p className="mt-1 text-sm text-gray-500">
                            Fast, secure and easy payment experience.
                        </p>

                        {/* Contact Details */}

                        <div className="mt-4 rounded-2xl border border-[#dbe7f4] bg-white p-4 shadow-sm sm:p-5">

                            <h2 className="text-xl font-semibold">
                                Contact Details
                            </h2>

                            <p className="mt-1 text-sm text-gray-500">
                                Please provide your billing information.
                            </p>

                            <div className="mt-4 grid gap-4 md:grid-cols-2">

                                {/* Full Name */}

                                <div>
                                    <label className="mb-2 block text-sm font-medium">
                                        Full Name <span className="text-red-500">*</span>
                                    </label>

                                    <div className="relative flex items-center">
                                        <Building2
                                            className="absolute left-3  text-gray-400"
                                            size={18}
                                        />
                                        <input
                                            type="text"
                                            ref={fullNameRef}
                                            name="fullName"
                                            value={formData.fullName}
                                            onChange={handleChange}
                                            placeholder="Full Name"
                                            className={`w-full rounded-xl border pl-10 pr-4 py-3 text-sm outline-none
    focus:border-green-500 focus:ring-1 focus:ring-green-500 sm:text-base ${errors.fullName
                                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                                    : ""
                                                }`}
                                        />
                                    </div>

                                    {errors.fullName && (
                                        <p className="mt-1 text-sm text-red-500">
                                            {errors.fullName}
                                        </p>
                                    )}
                                </div>

                                {/* Email */}

                                <div>
                                    <label className="mb-2 block text-sm font-medium">
                                        Email Address <span className="text-red-500">*</span>
                                    </label>

                                    <div className="relative flex items-center">
                                        <Mail
                                            className="absolute left-3  text-gray-400"
                                            size={18}
                                        />
                                        <input
                                            ref={emailRef}
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="abc@gmail.com"
                                            className={`w-full rounded-xl border pl-10 pr-4 py-3 text-sm outline-none
    focus:border-green-500 focus:ring-1 focus:ring-green-500 sm:text-base ${errors.email
                                                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                                    : ""
                                                }`}
                                        />
                                    </div>

                                    {errors.email && (
                                        <p className="mt-1 text-sm text-red-500">
                                            {errors.email}
                                        </p>
                                    )}
                                </div>

                            </div>

                            {/* Phone */}

                            <div className="mt-4">
                                <label className="mb-2 block text-sm font-medium">
                                    Mobile Number <span className="text-red-500">*</span>
                                </label>

                                <div className="relative  flex items-center">
                                    <Phone
                                        className="absolute left-3  text-gray-400"
                                        size={18}
                                    />
                                    <input
                                        ref={phoneRef}
                                        type="digit"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="+91 XXXXXXXXXX"
                                        className={`w-full rounded-xl border pl-10 pr-4 py-3 text-sm outline-none
    focus:border-green-500 focus:ring-1 focus:ring-green-500 sm:text-base ${errors.phone
                                                ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                                                : ""
                                            }`}
                                    />
                                </div>
                                {errors.phone && (
                                    <p className="mt-1 text-sm text-red-500">
                                        {errors.phone}
                                    </p>
                                )}
                            </div>

                        </div>

                        {/* Payment Details */}

                        <div className="mt-4 rounded-3xl border border-[#dbe7f4] bg-white p-4 shadow sm:p-5">

                            <h2 className="text-xl font-semibold">
                                Payment Details
                            </h2>

                            <p className="mt-1 text-sm text-gray-500">
                                Enter payment information.
                            </p>

                            <div className="mt-4 grid gap-4 md:grid-cols-2">

                                {/* Currency */}

                                <div>
                                    <label className="mb-2 block text-sm font-medium">
                                        Currency <span className="text-red-500">*</span>
                                    </label>

                                    <select
                                        name="currency"
                                        value={formData.currency}
                                        onChange={handleChange}
                                        className={`w-full rounded-xl border px-4 py-3 text-sm outline-none focus:border-[#1d4ed8] sm:text-base ${errors.currency
                                                ? "border-red-500 focus:border-red-500"
                                                : ""
                                            }`}
                                    >
                                        {CURRENCIES.map((item) => (
                                            <option key={item} value={item}>
                                                {item}
                                            </option>
                                        ))}
                                    </select>

                                    {errors.currency && (
                                        <p className="mt-2 text-sm text-red-500">
                                            {errors.currency}
                                        </p>
                                    )}
                                </div>

                                {/* AMOUNT */}
                                <div>
                                    <label className="mb-2 block text-sm font-medium">
                                        Service Amount{" "}
                                        <span className="text-red-500">*</span>
                                    </label>

                                    <div className="relative flex items-center">
                                        <span className="absolute left-3 text-gray-500 font-medium">
                                            ₹
                                        </span>

                                        <input
                                            ref={amountRef}
                                            type="digit"
                                            name="amount"
                                            value={formData.amount}
                                            onChange={handleChange}
                                            placeholder="0.00"
                                            step="0.01"
                                            min="1"
                                            readOnly={passedPrice !== undefined && passedPrice !== null}
                                            className={`w-full rounded-xl border py-3 pl-8 pr-4 outline-none transition-all
                ${passedPrice !== undefined && passedPrice !== null
                                                    ? "bg-gray-100 cursor-not-allowed"
                                                    : ""
                                                }
                ${errors.amount
                                                    ? "border-red-500"
                                                    : "border-gray-300"
                                                }
                focus:border-green-600 focus:ring-1 focus:ring-green-600
            `}
                                        />
                                    </div>

                                    {errors.amount && (
                                        <p className="mt-1 text-sm text-red-500">
                                            {errors.amount}
                                        </p>
                                    )}
                                </div>

                            </div>

                        </div>

                        {/* Agreement */}

                        <div className="mt-4 rounded-3xl border border-[#dbe7f4] bg-white p-4 shadow sm:p-5">

                            <label className="flex items-start gap-3">
                                <input
                                    ref={agreementRef}
                                    type="checkbox"
                                    name="agreement"
                                    checked={formData.agreement}
                                    onChange={handleChange}
                                    className={`mt-1 ${errors.agreement
                                            ? "ring-2 ring-red-500 ring-offset-2"
                                            : ""
                                        }`}
                                />
                                <span className="text-sm text-gray-600">
                                    I agree to the
                                    <a href="/equity-policy/termsandcondition" className="text-blue-600 underline hover:text-blue-800"> Terms & Conditions</a>
                                    {" and "}
                                    <a href="/equity-policy/refund-policy" className="text-blue-600 underline hover:text-blue-800">Refund Policy</a>.
                                </span>
                            </label>

                            {errors.agreement && (
                                <p className="mt-2 text-sm text-red-500">
                                    {errors.agreement}
                                </p>
                            )}
                        </div>

                    </div>

                    {/* RIGHT SIDE */}

                    <div className="flex justify-center lg:items-center lg:justify-end">

                        <div className="w-full max-w-none rounded-2xl bg-gradient-to-br from-[#0f172a] via-[#111827] to-[#0b3b2e] p-4 text-white shadow-lg lg:max-w-[340px]">

                            <h2 className="text-base font-semibold sm:text-lg">
                                Payment Summary
                            </h2>

                            <div className="mt-4 text-center">
                                <p className="text-sm text-gray-300">
                                    Total Payable
                                </p>

                                <h1 className="mt-2 text-2xl font-bold sm:text-3xl">
                                    {formData.currency} {ledger.total.toFixed(2)}
                                </h1>
                            </div>

                            <div className="mt-5 space-y-3 text-sm sm:mt-6 sm:text-base">

                                <div className="flex justify-between">
                                    <span>Base Price</span>
                                    <span>{ledger.amount.toFixed(2)}</span>
                                </div>

                                <div className="flex justify-between">
                                    <span>Subtotal</span>
                                    <span>{ledger.subtotal.toFixed(2)}</span>
                                </div>

                                <div className="flex justify-between">
                                    <span>GST (18%)</span>
                                    <span>{ledger.gst.toFixed(2)}</span>
                                </div>

                                <div className="flex justify-between">
                                    <span>Platform Fee</span>
                                    <span>{ledger.platformFee.toFixed(2)}</span>
                                </div>

                                <hr className="border-gray-700" />

                                <div className="flex justify-between text-lg font-bold">
                                    <span>Total</span>
                                    <span>{ledger.total.toFixed(2)}</span>
                                </div>

                            </div>

                            {/* v2 fallback captcha + error */}
                            {showV2 && (
                                <div className="mt-5">
                                    <p className="mb-2 text-xs text-gray-300">
                                        Please complete the additional verification below.
                                    </p>
                                    <CaptchaV2
                                        ref={captchaV2Ref}
                                        onChange={async (token) => {
                                            // eslint-disable-next-line no-console
                                            console.log(
                                                "[SecurePayment] v2 captcha onChange:",
                                                { present: Boolean(token) }
                                            );
                                            setCaptchaV2Token(token || "");
                                            setErrors((prev) => ({
                                                ...prev,
                                                captchaV2: token
                                                    ? ""
                                                    : "Please verify you are not a robot.",
                                            }));

                                            if (!token) {
                                                setIsVerified(false);
                                                return;
                                            }

                                            setIsVerifying(true);
                                            setIsVerified(false);
                                            try {
                                                const result =
                                                    await recaptchaRepository.verify(
                                                        {
                                                            version: "v2",
                                                            token,
                                                            action: "payment",
                                                        }
                                                    );
                                                // eslint-disable-next-line no-console
                                                console.log(
                                                    "[SecurePayment] v2 verify result:",
                                                    result
                                                );
                                                const wrapperOk =
                                                    result?.success === true;
                                                const innerOk =
                                                    result?.data?.success ===
                                                    true;
                                                if (wrapperOk && innerOk) {
                                                    setIsVerified(true);
                                                    setErrors((prev) => ({
                                                        ...prev,
                                                        captchaV2: "",
                                                    }));
                                                } else {
                                                    setIsVerified(false);
                                                    setErrors((prev) => ({
                                                        ...prev,
                                                        captchaV2:
                                                            result?.message ||
                                                            "Captcha verification failed. Please try again.",
                                                    }));
                                                    if (
                                                        captchaV2Ref.current?.reset
                                                    ) {
                                                        captchaV2Ref.current.reset();
                                                    }
                                                }
                                            } catch (err) {
                                                // eslint-disable-next-line no-console
                                                console.error(
                                                    "[SecurePayment] v2 verify error:",
                                                    err
                                                );
                                                setIsVerified(false);
                                                setErrors((prev) => ({
                                                    ...prev,
                                                    captchaV2:
                                                        "Captcha verification failed. Please try again.",
                                                }));
                                            } finally {
                                                setIsVerifying(false);
                                            }
                                        }}
                                        onExpired={() => {
                                            // eslint-disable-next-line no-console
                                            console.log(
                                                "[SecurePayment] v2 captcha expired"
                                            );
                                            setCaptchaV2Token("");
                                            setIsVerified(false);
                                            setErrors((prev) => ({
                                                ...prev,
                                                captchaV2: "Please verify you are not a robot.",
                                            }));
                                        }}
                                    />
                                </div>
                            )}

                            {errors.captchaV2 && (
                                <p className="mt-2 text-xs text-red-400">
                                    {errors.captchaV2}
                                </p>
                            )}

                            <button
                                type="submit"
                                disabled={!captchaReady || !isVerified || isVerifying}
                                className="mt-5 w-full rounded-xl bg-gradient-to-r from-[#1d4ed8] to-[#16a34a] py-3 text-sm font-semibold transition hover:opacity-95 disabled:opacity-60 disabled:cursor-not-allowed sm:mt-6 sm:text-base"
                            >
                                {isVerifying ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Verifying...
                                    </span>
                                ) : !isVerified ? (
                                    "Complete Captcha to Pay"
                                ) : (
                                    "Pay Now"
                                )}
                            </button>

                            <p className="mt-3 text-center text-xs text-gray-400">
                                Payments are securely processed by PayU.
                            </p>

                        </div>

                    </div>

                </form>

            </div>
            <Footer />

        </div>
        </>
    );
}

function validateField(name, value) {
    if (name === "fullName") {
        return value.trim() ? "" : "Full Name is required.";
    }
    if (name === "email") {
        if (!value.trim()) return "Email Address is required.";
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
            ? ""
            : "Please enter a valid email address.";
    }
    if (name === "currency") {
        return CURRENCIES.includes(value) ? "" : "Currency is required.";
    }
    if (name === "amount") {
        if (value === "" || value === null || value === undefined) {
            return "Base Price is required.";
        }
        return Number(value) > 0 ? "" : "Base Price must be greater than 0.";
    }
    if (name === "phone") {
        if (!value.trim()) return "Mobile Number is required.";
        if (!/^[6-9]\d{9}$/.test(value.trim())) {
            return "Please enter a valid 10-digit mobile number.";
        }
        return "";
    }
    if (name === "agreement") {
        return value
            ? ""
            : "You must accept the Terms & Conditions and Refund Policy.";
    }
    return "";
}

function validateForm(data) {
    const nextErrors = {};
    [
        "fullName",
        "email",
        "currency",
        "amount",
        "agreement",
        "phone",
    ].forEach((key) => {
        const err = validateField(key, data[key]);
        if (err) nextErrors[key] = err;
    });
    return nextErrors;
}
